import type { CardDetailsFragment } from '@graphql/fragments/cards/core.graphql';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { exec, match, parse } from 'matchit';

import { type CardPageRouteInfo, type PageMode, PageModes, type PageSection } from '../types/types';
import type { Card, CardField, CardImage, CardListProperty, CardProperty } from '.cache/__types__';

export const ACCEPTED_ROUTES = {
  // template routes
  ALL_TEMPLATES: ':workspaceId/cards/templates',
  TEMPLATE_CARD_PREVIEW: ':workspaceId/cards/templates/:templateId/preview',
  NEW_TEMPLATE: ':workspaceId/cards/templates/new',
  TEMPLATE_EDIT: ':workspaceId/cards/templates/:templateId/edit',
  TEMPLATE_EDIT_DRAFT: ':workspaceId/cards/templates/:templateId/edit/:draftId',
  // team card routes
  NEW_TEAM_CARD: ':workspaceId/cards/team/new',
  TEAM_CARD: ':workspaceId/cards/team/:cardId/preview',
  TEAM_CARD_EDIT_REDIRECT: ':workspaceId/cards/team/:cardId/edit',
  TEAM_CARD_EDIT: ':workspaceId/cards/team/:cardId/edit/:draftId',
  TEAM_CARD_BULK_UPLOAD: ':workspaceId/cards/team/bulk-upload/select-template',
  // personal cards
  NEW_PERSONAL_CARD: `:workspaceId/cards/personal/new`,
  PERSONAL_CARD: `:workspaceId/cards/personal/:cardId/preview`,
  EDIT: `:workspaceId/cards/personal/:cardId/edit`,
  EDIT_DRAFT: `:workspaceId/cards/personal/:cardId/edit/:draftId`,
  //
};

export function normalisePageMode(
  modeQueryParam: string | string[] | undefined,
): Exclude<PageMode, 'create'> {
  switch (modeQueryParam) {
    case 'view':
      return PageModes.View;
    case 'edit':
      return PageModes.Edit;
    default:
      return PageModes.View;
  }
}

export function normaliseTab(tabQueryParam: string | string[] | undefined): PageSection {
  switch (tabQueryParam) {
    case 'card':
      return 'card';
    case 'qr-code':
      return 'qr-code';
    case 'email-signature':
      return 'email-signature';
    default:
      return 'card';
  }
}

export function getPathMatch(
  pathname: string,
  routes: string[],
  onMatch: (matchingRoute: string | undefined, values: Record<string, string>) => CardPageRouteInfo,
): CardPageRouteInfo {
  const parsedRoutes = routes.map(parse);
  const matches = match(pathname, parsedRoutes);
  const values = exec(pathname, matches);
  if (matches.length === 0) {
    throw new Error(`No matching route found for ${pathname}`);
  }
  return onMatch(matches[0].old, values);
}

export type CardAttribute = CardField | CardProperty | CardImage | CardListProperty;

export type CardAttributes = CardAttribute[];

const ignoredRequiredAttributes = [
  'title',
  'color',
  'honorific_prefix',
  'honorific_suffix',
  'last_name',
  'maiden_name',
  'middle_name',
  'preferred_name',
  'pronouns',
  'layout',
];

/**
 * Takes a Card, returns an array with all the fields, images, properties and list properties
 */
export function flattenAttributes(card: Maybe<CardDetailsFragment>): CardAttributes | undefined {
  if (!card) {
    return undefined;
  }

  // add fields to array
  const result: CardAttributes = [...(card.fields || [])];

  // add images to array
  if (card.images?.company_logo) {
    result.push(card.images.company_logo);
  }
  if (card.images?.cover_photo) {
    result.push(card.images.cover_photo);
  }
  if (card.images?.profile_picture) {
    result.push(card.images.profile_picture);
  }

  // add properties and list properties (list properties are inside properties)
  const newCardPropertiesArray: (CardProperty | CardListProperty)[] = Object.entries(card.properties ?? {})
    .map(([key, attr]) => {
      if (attr !== null && typeof attr === 'object' && 'value' in attr) {
        return attr as CardProperty;
      }

      if (attr !== null && typeof attr === 'object' && 'values' in attr) {
        return attr as CardListProperty;
      }

      return undefined;
    })
    .filter((attr): attr is CardProperty | CardListProperty => attr !== undefined);

  result.push(...newCardPropertiesArray);

  return result;
}

/**
 * Takes an array of card attributes, returns an array with only required attributes that are missing
 */
export function onlyMissingAndRequiredAttributes(
  card: Maybe<CardDetailsFragment>,
): CardAttributes | undefined {
  if (!card) {
    return undefined;
  }

  const attributes = flattenAttributes(card);

  if (!attributes) {
    return undefined;
  }

  return attributes.filter((attr) => {
    // ignore unwanted required attributes
    if (attr.attribute_type?.name && ignoredRequiredAttributes.includes(attr.attribute_type?.name)) {
      return false;
    }

    // ignore all shared attributes
    if (attr.value_provider === 'template') {
      return false;
    }

    // for fields and properties
    if (attr !== null && typeof attr === 'object' && 'value' in attr) {
      return !attr.value && !attr.is_optional;
    }

    // for images
    if (attr !== null && typeof attr === 'object' && 'edited_url' in attr) {
      return !attr.edited_url && !attr.is_optional;
    }

    // for list properties
    if (attr !== null && typeof attr === 'object' && 'values' in attr) {
      // i need to check if the lenth of values is 1 or more, then it shoudl be truthy, and if it's an empty array, it should be falsey
      return !attr.values?.length && !attr.is_optional;
    }

    return false;
  });
}

/**
 * Takes an array of card attributes, returns an array with only required attributes
 */
export function onlyRequiredAttributes(card: CardDetailsFragment): CardAttributes | undefined {
  if (!card) {
    return undefined;
  }

  const attributes = flattenAttributes(card);

  if (!attributes) {
    return undefined;
  }

  return attributes?.filter((attr) => {
    // ignore unwanted required attributes
    if (attr.attribute_type?.name && ignoredRequiredAttributes.includes(attr.attribute_type?.name)) {
      return false;
    }

    // ignore all shared attributes
    if (attr.value_provider === 'template') {
      return false;
    }

    // for fields, properties, images and list properties
    // it doesn't matter which one it is, they will all have an is_optional property
    if (attr !== null && typeof attr === 'object') {
      return !attr.is_optional;
    }

    return false;
  });
}

/**
 * Takes an array of card attributes, returns an array that has those attributes ordered by category rank and rank
 */
export function requiredAttributesSortedByRank(card: CardDetailsFragment): CardAttributes | undefined {
  if (!card) {
    return undefined;
  }

  const requiredAttributes = onlyRequiredAttributes(card);

  if (!requiredAttributes) {
    return undefined;
  }

  return requiredAttributes.sort((a, b) => {
    const rankA = a?.attribute_type?.category_rank || 0;
    const rankB = b?.attribute_type?.category_rank || 0;

    // sort by category rank first
    if (rankA < rankB) {
      return -1;
    }

    if (rankA > rankB) {
      return 1;
    }

    // If category_rank is equal, sort by the 'rank' property within 'attribute_type'
    const innerRankA = a?.attribute_type?.rank || 0;
    const innerRankB = b?.attribute_type?.rank || 0;

    if (innerRankA < innerRankB) {
      return -1;
    }

    if (innerRankA > innerRankB) {
      return 1;
    }

    return 0;
  });
}

/**
 * Will create a file name in the following format:
 * [firstName]_[lastName]_[cardId]_qrcode
 * [firstName]_[cardId]_qrcode
 * [email]_[cardId]_qrcode
 */
export function getBlinqQRCodeFileName(card: Card | undefined | null): string {
  let filename = '';

  if (card?.properties?.first_name?.value) {
    filename += card.properties.first_name.value;
  }

  if (card?.properties?.last_name?.value) {
    filename += card.properties.last_name.value;
  }

  if (!card?.properties?.first_name?.value && !card?.properties?.last_name?.value) {
    filename += card?.profile?.email;
  }

  if (card?.id) {
    if (filename.length !== 0) {
      filename += '_';
    }
    filename += card.id;
  }

  if (filename.length > 0) {
    filename += '_';
  }

  filename += 'qrcode';

  return filename;
}

// checks if user is using a Mac
export function isMac(): boolean {
  if (typeof window !== 'undefined') {
    return /Macintosh/.test(window.navigator.userAgent);
  }
  return false;
}
